import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, ExternalLink, Clock, CheckCircle2, Shield } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { formatCurrency } from '../utils/helpers';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

// Mirrors daysPassedFromCurrentDate in index.js
function daysPassedFromCurrentDate(dateStr) {
  try {
    const inputDate = new Date(dateStr);
    const currentDate = new Date();
    const timeDifference = currentDate - inputDate;
    const millisecondsInDay = 1000 * 60 * 60 * 24;
    return Math.floor(timeDifference / millisecondsInDay);
  } catch {
    return null;
  }
}

const Deposit = () => {
  const navigate = useNavigate();
  const { user: contextUser } = useUser();
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [withdrawalCountdown, setWithdrawalCountdown] = useState(null); // { daysLeft, passed, total }
  const [loadingUserData, setLoadingUserData] = useState(true);

  useEffect(() => {
    if (!contextUser?.telegramName) { navigate('/login'); return; }
    if (!contextUser.email) { navigate('/email-verification?locPay=deposit'); return; }
    fetchUserData(contextUser.telegramName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser, navigate]);

  // Fetches fresh user data (including lastDepsit) — mirrors retriveAccountLocalUpdate for dashbord.html
  const fetchUserData = async (username) => {
    try {
      const response = await fetch(`${API_BASE}/users/${username}`);
      const data = await response.json();
      const info = data?.usernameInfo;

      if (response.status === 200 && info) {
        localStorage.setItem('UserDate', JSON.stringify(info));
        // Mirrors setIndexInfo() logic for dashbord.html — reads lastDepsit and shows countdown
        if (info?.lastDepsit) {
          const daysPassed = daysPassedFromCurrentDate(info.lastDepsit);
          const daysLeft = 7 - daysPassed;
          setWithdrawalCountdown({ daysLeft: Math.max(0, daysLeft), passed: daysPassed, total: 7 });
        } else {
          setWithdrawalCountdown(null);
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoadingUserData(false);
    }
  };

  const quickAmounts = [10, 25, 50, 100, 250, 500];

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
    setCustomAmount('');
  };

  const handleCustomAmount = (value) => {
    setCustomAmount(value);
    setAmount('');
  };

  // Mirrors handelSubmitDeposit in index.js
  const handleDeposit = () => {
    const depositAmount = amount || customAmount;
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      toast.error('Please enter a valid deposit amount');
      return;
    }
    const depositUrl = `https://tradebot-k4nt.onrender.com/?username=${contextUser.telegramName}&deposit=${depositAmount}`;
    toast.success(`Opening deposit page for ${formatCurrency(depositAmount)}`);
    window.open(depositUrl, '_blank');
  };

  if (!contextUser || loadingUserData) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
        </div>
      </DashboardLayout>
    );
  }

  const progressPercent = withdrawalCountdown
    ? Math.min(100, Math.round((withdrawalCountdown.passed / withdrawalCountdown.total) * 100))
    : 100;
  const canWithdraw = !withdrawalCountdown || withdrawalCountdown.daysLeft <= 0;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <DollarSign className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Deposit Funds</h1>
            <p className="text-slate-600">Add money to your MODANMIC account</p>
          </div>
        </div>

        {/* Withdrawal countdown notification — mirrors setIndexInfo() / pendingId in index.js */}
        {withdrawalCountdown && !canWithdraw && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="text-amber-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 text-lg mb-1">
                  {/* Mirrors: `${getPastDays} days Untile Withdrwal` from index.js */}
                  {withdrawalCountdown.daysLeft} day{withdrawalCountdown.daysLeft !== 1 ? 's' : ''} Until Withdrawal
                </h3>
                <p className="text-amber-700 text-sm mb-4">
                  A 7-day hold is placed on funds after each deposit. Your funds are safe — you just can't withdraw yet.
                </p>
                {/* Progress bar */}
                <div className="mb-1.5">
                  <div className="flex justify-between text-xs text-amber-600 mb-1.5">
                    <span>Last deposit</span>
                    <span>{withdrawalCountdown.passed} / 7 days passed</span>
                  </div>
                  <div className="w-full bg-amber-200 rounded-full h-3">
                    <div
                      className="bg-amber-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  Withdrawal available after {withdrawalCountdown.daysLeft} more day{withdrawalCountdown.daysLeft !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal available notification */}
        {canWithdraw && withdrawalCountdown !== null && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <CheckCircle2 className="text-green-500 flex-shrink-0" size={22} />
            <p className="text-green-800 font-medium text-sm">
              ✅ Your funds are fully available for withdrawal anytime.
            </p>
          </div>
        )}

        {/* Current Balance */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
          <p className="text-sm text-blue-700 mb-1">Current Balance</p>
          <p className="text-3xl font-bold text-blue-900">
            {contextUser.accountBalance || '$0.00'}
          </p>
        </div>

        {/* Quick Amounts */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-semibold text-slate-900 mb-4">Quick Amounts</h3>
          <div className="grid grid-cols-3 gap-3">
            {quickAmounts.map((value) => (
              <button
                key={value}
                onClick={() => handleQuickAmount(value)}
                className={`p-4 border-2 rounded-xl font-semibold transition-all ${
                  amount === value.toString()
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                ${value}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-semibold text-slate-900 mb-4">Custom Amount</h3>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="number"
              step="0.01"
              placeholder="Enter amount"
              value={customAmount}
              onChange={(e) => handleCustomAmount(e.target.value)}
              className="w-full pl-11 pr-4 py-4 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all text-lg font-semibold"
            />
          </div>
        </div>

        {/* Deposit Button */}
        <button
          onClick={handleDeposit}
          disabled={!amount && !customAmount}
          className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <ExternalLink size={20} />
          Proceed to Payment
        </button>

        {/* 7-Day Policy Notice */}
        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-slate-500 mt-0.5 flex-shrink-0" size={18} />
            <div>
              <h3 className="font-semibold text-slate-800 mb-1 text-sm">7-Day Withdrawal Hold Policy</h3>
              <p className="text-slate-600 text-sm">
                After each deposit, a 7-day security hold is placed on withdrawals. You can still send, invest, and exchange during this period.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-slate-500">
          Depositing as: <span className="font-semibold text-slate-900">@{contextUser.telegramName}</span>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Deposit;
